import { v4 as uuidv4 } from "uuid"
import type {
  DialogHandle,
  DialogOpenOptions,
  DialogStoreListener,
  ManagedDialogComponent,
  OpenDialogsEntry,
} from "./types"
import { DIALOG_CLOSE_ANIMATION_MS } from "./const"

// ------------------------------------------------------------
// Store implementation
// ------------------------------------------------------------
const openDialogs: OpenDialogsEntry[] = []

let openDialogsSnapshot: OpenDialogsEntry[] = [...openDialogs]
const listeners = new Set<DialogStoreListener>()

export function getOpenDialogsSnapshot() {
  return openDialogsSnapshot
}

export function subscribeToOpenDialogs(listener: DialogStoreListener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function emitStoreChange() {
  openDialogsSnapshot = [...openDialogs]
  listeners.forEach((listener) => listener())
}

// ------------------------------------------------------------
// Opening and closing dialogs
// ------------------------------------------------------------
export function openDialog<Props extends object>(options: DialogOpenOptions<Props>): DialogHandle {
  const id = uuidv4()
  const props = (options.props ?? {}) as Props

  openDialogs.push({
    id,
    isOpen: true,
    persistent: options.persistent,
    component: options.component as ManagedDialogComponent<object>,
    props: props as object,
    onClose: options.onClose,
  })

  emitStoreChange()

  return {
    id,
    close: () => closeDialogById(id),
  }
}

export function closeTopDialog() {
  if (openDialogs.length === 0) {
    return
  }

  const topDialog = [...openDialogs].reverse().find((dialog) => dialog.isOpen)
  if (!topDialog) {
    return
  }

  markDialogClosed(topDialog.id)
}

export function closeDialogById(id: string) {
  markDialogClosed(id)
}

export function closeAllDialogs() {
  if (openDialogs.length === 0) {
    return
  }

  openDialogs
    .filter((dialog) => dialog.isOpen)
    .map((dialog) => dialog.id)
    .forEach(markDialogClosed)
}

// ------------------------------------------------------------
// Closing dialogs scheduler

// This is needed to keep the dialog components mounted during the closing animation,
// otherwise the animation can't run at all because the component is unmounted immediately
// when we mark it as closed in the store.
// ------------------------------------------------------------
const dialogRemovalTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

function clearDialogRemovalTimeout(id: string) {
  const timeout = dialogRemovalTimeouts.get(id)
  if (!timeout) {
    return
  }

  clearTimeout(timeout)
  dialogRemovalTimeouts.delete(id)
}

function markDialogClosed(id: string) {
  const dialog = openDialogs.find((entry) => entry.id === id && entry.isOpen)
  if (!dialog) {
    return
  }

  dialog.isOpen = false
  const onClose = dialog.onClose
  dialog.onClose = undefined
  onClose?.()

  emitStoreChange()
  scheduleDialogRemoval(id)
}

function scheduleDialogRemoval(id: string) {
  clearDialogRemovalTimeout(id)

  const timeout = setTimeout(() => {
    removeDialogById(id)
  }, DIALOG_CLOSE_ANIMATION_MS)

  dialogRemovalTimeouts.set(id, timeout)
}

function removeDialogById(id: string) {
  const dialogIndex = openDialogs.findIndex((dialog) => dialog.id === id)

  if (dialogIndex === -1) {
    return
  }

  clearDialogRemovalTimeout(id)
  openDialogs.splice(dialogIndex, 1)
  emitStoreChange()
}
